/**
 * profileEdit — the profile modal's Save actually saves the name and the handle.
 *
 * REPORTED BY BLAKE, 2026-08-31: "I tried to change my username and noticed it
 * doesn't save." The handler (app.js:4526) read `#edit-name` and `#edit-handle`
 * only to VALIDATE them, then threw both away; only the bio was assigned, and
 * only to a module-level variable that dies on reload. No fetch on any branch.
 *
 * Unlike the listing modal, `#edit-save` was in NEITHER guard list — so it lied
 * with nothing suppressing it. It is now in GUARDED_WRITES *and* wired here.
 *
 * ══ 🔴 REWRITTEN 2026-08-31, SAME DAY, BECAUSE THE FIRST VERSION DID NOTHING ══
 *
 * The first version read `window.store.me`, `window.ACCOUNT.identity` and
 * `window.profileBio`. Those are LEXICAL bindings in a classic script — script
 * scope, never `window` — so every read was `undefined` and the save silently
 * did nothing at all. Same defect, same hour, as listingEdit.ts; see its header
 * for the full account.
 *
 * 🪤 The tests passed because the harness ASSIGNED those globals itself. A
 * fixture that manufactures the condition under test proves only that the
 * fixture is self-consistent. Now tested against the real bundle.
 *
 * ══ EVERYTHING COMES FROM THE DOM ══
 *
 * The modal renders the current values into its own inputs (app.js:3670-3672),
 * so snapshotting them when it OPENS gives the "before" without touching script
 * scope. Comparing on save gives what changed.
 *
 * ══ 🔑 CAPTURE PHASE ══
 *
 * `#edit-save` is guarded, so the guard stopPropagation()s on document. A bubble
 * listener would never fire. Capture runs first; stopPropagation (not
 * stopImmediate) lets both run — ours saves, the guard keeps the fabricator dead.
 *
 * ══ 🪤 IDENTITY GOES ONLY WHEN EDITED ══
 *
 * `username` 409s on conflict (users.ts:344). Sending an unchanged handle on
 * every save would let an unrelated BIO edit fail with "that username is already
 * taken" — a refusal the person cannot act on because they never touched it.
 *
 * ⚠️ A SECOND username field exists on the account page (`#acct-handle`, wired by
 * accountSettings.ts). Both PATCH /api/users/me. That duplication is Blake's
 * markup and is left alone — but it is why the report had to be pinned to a
 * SCREEN before building. This module owns the PROFILE modal only.
 *
 * ══ WHY A RELOAD ══
 *
 * The guard suppresses the handler that closes the modal and repaints, and the
 * username appears in a dozen render sites. Re-booting re-reads identity from
 * the server, which is the only way the screen can honestly agree with it.
 */

const OPENER_SELECTOR = "#edit-profile";
const SAVE_SELECTOR = "#edit-save";
const CANCEL_SELECTOR = "#edit-cancel";

export const SAVED_MESSAGE = "Profile updated";
export const SIGNIN_MESSAGE = "Sign in again to save your profile.";
export const DISPLAY_NAME_REQUIRED = "Display name can't be empty";
export const USERNAME_TOO_SHORT = "Usernames need at least 3 characters";
export const OFFLINE_MESSAGE = "Could not save your profile. Check your connection and try again.";
export const FAILED_MESSAGE = "Could not save your profile. Try again.";

export interface ProfileFields {
  displayName: string;
  username: string;
  bio: string;
}

export interface ProfilePatch {
  displayName?: string;
  username?: string;
  bio?: string;
}

/** Read the modal's three fields straight out of the DOM. Exported for tests. */
export function readProfileFields(doc: Document): ProfileFields {
  const val = (id: string): string => {
    const el = doc.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | null;
    return el?.value?.trim() ?? "";
  };
  return {
    displayName: val("edit-name"),
    username: val("edit-handle"),
    bio: val("edit-bio"),
  };
}

/**
 * Build the PATCH body from what changed. Pure — exported for tests.
 *
 * Bio may be CLEARED to empty, unlike the other two: removing your bio is a real
 * intent the modal can express, whereas a blank display name is refused outright
 * and a blank handle is not a handle.
 */
export function buildProfilePatch(initial: ProfileFields, next: ProfileFields): ProfilePatch {
  const patch: ProfilePatch = {};
  if (next.displayName && next.displayName !== initial.displayName) patch.displayName = next.displayName;
  if (next.username && next.username !== initial.username) patch.username = next.username;
  if (next.bio !== initial.bio) patch.bio = next.bio;
  return patch;
}

interface ProfileWindow extends Window {
  toast?: (message: string) => void;
}

export function installProfileEdit(
  win: ProfileWindow,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  {
    notify = (message: string) => {
      if (!win.document.getElementById("toast")) return;
      win.toast?.(message);
    },
    reload = () => win.location.reload(),
    reloadDelayMs = 700,
    defer = (fn: () => void) => queueMicrotask(fn),
  }: {
    notify?: (m: string) => void;
    reload?: () => void;
    reloadDelayMs?: number;
    defer?: (fn: () => void) => void;
  } = {},
): () => void {
  let busy = false;
  let initial: ProfileFields | null = null;

  async function serverError(res: Response, fallback: string): Promise<string> {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    return body.error ?? fallback;
  }

  async function save(): Promise<void> {
    if (busy) return;
    if (!initial) return;

    const next = readProfileFields(win.document);

    /* Blake's two client-side checks, kept in effect. Shape rules the modal can
       answer itself, not policy — the server still owns length, charset and
       uniqueness, and its 409 is what actually decides a handle.

       🪤 THE NAME CHECK FIRES ONLY ON A CLEAR, NOT ON AN ABSENCE. Blake's version
       was `if (!name) refuse`, which reads fine until you meet an account with
       no display name to begin with: ACCOUNT.identity.displayName is populated
       by installAccount from the API (floorTransport:1386), and
       mapAccountIdentity falls back to "" when the server sends null — so such
       a person could never save a USERNAME change either, blocked by a field
       they never touched. Refusing only when they HAD a name and blanked it
       keeps the rule Blake intended and drops the collateral.

       Caught by the live tests, not by reasoning: the demo's own ACCOUNT carries
       no display name, so eight assertions failed on a guard that had nothing
       to do with what they were testing. */
    if (initial.displayName && !next.displayName) { notify(DISPLAY_NAME_REQUIRED); return; }
    if (next.username && next.username.length < 3) { notify(USERNAME_TOO_SHORT); return; }

    const patch = buildProfilePatch(initial, next);

    if (Object.keys(patch).length === 0) {
      /* Close as expected, but claim nothing — "Profile updated" over an
         unchanged profile is a smaller lie than the one being fixed, still a lie. */
      (win.document.querySelector(CANCEL_SELECTOR) as HTMLElement | null)?.click();
      return;
    }

    busy = true;
    try {
      const res = await authFetch("/api/users/me", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });

      if (res.status === 401) { notify(SIGNIN_MESSAGE); return; }
      if (!res.ok) {
        /* 409 is a taken username — the one failure the person can act on, and
           the reason identity is only sent when edited. */
        notify(await serverError(res, FAILED_MESSAGE));
        return;
      }

      notify(SAVED_MESSAGE);
      win.setTimeout(reload, reloadDelayMs);
    } catch {
      notify(OFFLINE_MESSAGE);
    } finally {
      busy = false;
    }
  }

  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (!target?.closest) return;

    if (target.closest(OPENER_SELECTOR)) {
      /* Deferred: we run in CAPTURE, before app.js sets editOpen and re-renders,
         so the modal does not exist yet. A microtask lands after that render. */
      defer(() => { initial = readProfileFields(win.document); });
      return;
    }

    if (target.closest(SAVE_SELECTOR)) {
      void save();
    }
  };

  win.document.addEventListener("click", onClick, true);
  return () => win.document.removeEventListener("click", onClick, true);
}
